import React from 'react';
import {StyleSheet, findNodeHandle} from 'react-native';
import {Heading, Box, Center} from 'native-base';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {
  useForm,
  SubmitHandler,
  SubmitErrorHandler,
  FieldErrors,
  FieldError,
} from 'react-hook-form';
import {
  DateInput as Date,
  FileInput as File,
  MaskedInput,
  DropDownInput,
  TextArea,
  ReadonlyInput,
  ReadonlyTextArea,
} from '../../../../../components/inputs';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../../../../components/buttons';
import IDropDownOption from '../../../../../interfaces/ui/dropdown-option.interface';
import useToast from '../../../../../hooks';
import {IPaymentForm} from '../../../../../interfaces/forms';
import {checkIsValidFile} from '../../../../../utils/validations';
import {ProofType} from '../../../../../constants/enumerations';
import {message} from '../../../../../utils/messages';
import {useContextState} from '../../../../../context/user';
import {isWebApp} from '../../../../../utils/platform/is-web';
import {ResultOfRequestCached} from '../../../../../interfaces/types/result-of-request-cached';
import {IListOption} from '../../../../../context/user/Types';

interface FormProps {
  primaryButtonLabel?: string;
  invoice?: {
    code: string;
    fees: string;
  };
  onSubmit?: (
    transaction: IPaymentForm,
    type: ProofType,
  ) => Promise<boolean | ResultOfRequestCached>;
  onEndSubmit?: (success: boolean | ResultOfRequestCached) => void;
}

const PaymentForm = ({
  onSubmit: launchSubmitEvent,
  onEndSubmit: launchEndSubmitEvent,
  invoice,
  primaryButtonLabel,
}: FormProps) => {
  const {
    control,
    handleSubmit,
    formState: {errors},
    reset,
    watch,
  } = useForm({
    defaultValues: {
      amount: undefined,
      date: undefined,
      source: undefined,
      bank: undefined,
      voucher: undefined,
      file: undefined,
      comments: undefined,
    } as IPaymentForm,
  });

  const sourceBankId = watch('source');

  const {banks, sourceBanks} = useContextState();
  const {showInputsError} = useToast();

  const onSubmit: SubmitHandler<IPaymentForm> = async (data: IPaymentForm) => {
    if (launchSubmitEvent) {
      const success = await launchSubmitEvent(data, ProofType.payment);
      if (
        success !== false &&
        (success as ResultOfRequestCached).status !== 'fail'
      ) {
        handleClearForm();
      }
      if (launchEndSubmitEvent) {
        launchEndSubmitEvent(success);
      }
    }
  };

  const onInvalid: SubmitErrorHandler<IPaymentForm> = async (
    fieldErrors: FieldErrors<IPaymentForm>,
  ) => {
    if (fieldErrors) {
      showInputsError();
    }
  };

  const handleDateSelect = () => {};

  const getBankList = (): IDropDownOption[] => {
    if (sourceBankId) {
      const sourceBank = sourceBanks.find(
        item => item.source?.value === sourceBankId,
      );

      if (sourceBank) {
        const filtered = banks.filter((item: IListOption) => {
          return sourceBank.source?.foreign === item.source?.foreign;
        });

        return filtered.map((item: IListOption) => {
          return {
            label: item.title,
            value: item.value,
          };
        });
      }
    }
    return [];
  };

  const getSourceBankList = (): IDropDownOption[] => {
    return sourceBanks.map(bank => ({
      label: bank.title,
      value: bank.value,
    }));
  };

  const handleClearForm = () => {
    reset();
  };

  return (
    <Box flex={1}>
      <KeyboardAwareScrollView
        style={styles.content}
        enableOnAndroid={true}
        scrollEnabled={true}
        enableAutomaticScroll={true}
        showsVerticalScrollIndicator={isWebApp}
        extraHeight={100}
        extraScrollHeight={100}
        innerRef={ref => {
          (this as any).scroll = ref;
        }}>
        <Center width="100%" flex={1} justifyContent="flex-start">
          <Box width="100%" py={5}>
            <Heading
              size="xs"
              paddingBottom="3"
              fontWeight="bold"
              color="#06355E">
              {message('proof-voucher-subtitle-payment')}
            </Heading>
            <DropDownInput
              control={control}
              name="source"
              label="proof-label-input-bank-source"
              options={getSourceBankList()}
              error={errors.source}
              rules={{
                required: 'form-is-required',
              }}
            />
            <MaskedInput
              control={control}
              name="amount"
              label="proof-label-input-amount"
              mask="money"
              error={errors.amount}
              rules={{
                required: 'form-is-required',
              }}
            />
            <Date
              control={control}
              name="date"
              label="proof-label-input-date"
              onConfirm={handleDateSelect}
              error={errors.date}
              rules={{
                required: 'form-is-required',
              }}
            />
            <DropDownInput
              control={control}
              name="bank"
              label="proof-label-input-bank-target"
              options={getBankList()}
              error={errors.bank}
              rules={{
                required: 'form-is-required',
              }}
            />
            <MaskedInput
              control={control}
              name="voucher"
              label="proof-label-input-voucher"
              mask="voucher"
              error={errors.voucher}
              rules={{
                required: 'form-is-required',
              }}
            />
            <TextArea
              control={control}
              name="comments"
              label="proof-label-input-comment"
              error={errors.comments}
              numberOfLines={4}
              maxLength={250}
              onContentSizeChange={event => {
                var that: any = this;
                if (that && that.scrollRef && that.scrollRef.props) {
                  that.scrollRef.props.scrollToFocusedInput(
                    findNodeHandle(event.target),
                  );
                }
              }}
              rules={{
                required: 'form-is-required',
              }}
            />
            <File
              control={control}
              name="file"
              label="proof-label-input-voucher-file"
              info={{
                before: 'proof-info-input-voucher-file-before',
                after: 'proof-info-input-voucher-file-after',
              }}
              error={errors?.file as FieldError}
              rules={{
                required: 'form-is-required',
                validate: checkIsValidFile,
              }}
              style={styles.file}
            />

            {invoice && (
              <Box marginTop={5}>
                <ReadonlyInput
                  label="screen-c2p-payment-invoice-number-label"
                  value={invoice.code}
                  backgroundColor="#F8F8F8"
                />

                <ReadonlyTextArea
                  label="screen-c2p-payment-invoice-fees-label"
                  value={invoice.fees}
                  numberOfLines={5}
                  maxLength={250}
                  backgroundColor="#F8F8F8"
                />
              </Box>
            )}
            <Box mt={invoice ? '2' : '10'}>
              <PrimaryButton
                label={
                  primaryButtonLabel ? primaryButtonLabel : 'sign-up-proof-send'
                }
                onPress={handleSubmit(onSubmit, onInvalid)}
              />
            </Box>
            <Box mt={5}>
              <SecondaryButton
                label="proof-button-cancel"
                onPress={handleClearForm}
              />
            </Box>
          </Box>
        </Center>
      </KeyboardAwareScrollView>
    </Box>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  file: {
    marginTop: 15,
  },
});

export default PaymentForm;
